/**
 * Buscador de pantallas (Ctrl/Cmd+K). Sale de la misma lista de items que el sidebar
 * (`lib/navItems.ts`) -- ya viene filtrada por lo que esta persona puede ver, asi que
 * nunca ofrece una pantalla a la que no tiene acceso.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface PaletteItem {
  key: string;
  label: string;
  to: string;
  groupLabel?: string;
}

export function CommandPalette({ items }: { items: PaletteItem[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.label.toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => setActiveIdx(0), [filtered]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (!open) return;
      if (e.key === 'Escape') setOpen(false);
      else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[activeIdx];
        if (item) {
          setOpen(false);
          navigate(item.to);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, filtered, activeIdx, navigate]);

  return (
    <>
      <button
        className="btn btn--ghost"
        aria-label="Buscar pantalla (Ctrl+K)"
        title="Buscar pantalla (Ctrl+K)"
        onClick={() => setOpen(true)}
        style={{ padding: '6px 8px' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Buscar pantalla"
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,17,17,.5)', display: 'flex', justifyContent: 'center', paddingTop: '12vh', zIndex: 100 }}
          onClick={() => setOpen(false)}
        >
          <div className="card" style={{ width: 'min(480px, 92vw)', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar una pantalla…"
              style={{ border: 'none', borderBottom: '1px solid var(--border)', background: 'transparent', color: 'var(--ink)', font: '15px var(--f-ui)', padding: '14px 16px', outline: 'none' }}
            />
            <div style={{ overflowY: 'auto', padding: 6 }}>
              {filtered.length === 0 ? (
                <p className="muted" style={{ padding: '20px 14px', fontSize: 12.5, margin: 0, textAlign: 'center' }}>
                  Nada que coincida con «{query}»
                </p>
              ) : (
                filtered.map((it, i) => (
                  <div
                    key={it.key}
                    role="option"
                    aria-selected={i === activeIdx}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => {
                      setOpen(false);
                      navigate(it.to);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '9px 10px',
                      borderRadius: 6,
                      fontSize: 13,
                      cursor: 'pointer',
                      background: i === activeIdx ? 'var(--brand-weak)' : 'transparent',
                      color: i === activeIdx ? 'var(--brand-ink)' : 'var(--ink)',
                    }}
                  >
                    <span>{it.label}</span>
                    {it.groupLabel && (
                      <span className="mono" style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ink-muted)' }}>
                        {it.groupLabel}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: 12, padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--ink-muted)' }}>
              <span>↑↓ para moverte</span>
              <span>Enter para ir</span>
              <span>Esc para cerrar</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
